import styled, { css } from 'styled-components';
import { spin } from 'Styles/effects';
import { VARIANTS, SIZES } from './Button.constants';

/**
 * `theme.buttons.tertiary.color`/`.border` are the muted, inactive-looking tokens (the ones
 * `SearchBar/SaveFilter` reaches for on its own `disabled` case) — `.colorActive`/`.borderActive`
 * are the real enabled-state ones the legacy `Cancel` button actually rendered with. `secondary`
 * here is never disabled through this path (isDisabled already routes to theme.buttons.disabled
 * above), so it must always read the *Active tokens, never the muted ones.
 */
const resolveSolidTokens = (theme, variant, isDisabled) => {
  if (isDisabled) return theme.buttons.disabled;
  if (variant === VARIANTS.SECONDARY) {
    return { background: theme.buttons.tertiary.background, color: theme.buttons.tertiary.colorActive };
  }
  return theme.buttons.primary;
};

/**
 * `primary` never shows a border (matches the legacy `BlueButton` look it replaces) —
 * only `secondary` keeps a visible one. Transparent (not `none`) so the 1px is still
 * reserved and both variants render at the same size side by side.
 */
const resolveBorderColor = (theme, variant, isDisabled) => {
  if (variant !== VARIANTS.SECONDARY) return 'transparent';
  return isDisabled ? theme.buttons.disabled.border : theme.buttons.tertiary.borderActive;
};

const SOLID_SIZE = {
  [SIZES.SM]: { height: '28px', paddingKey: 'sm', fontSizeKey: 'sm' },
  [SIZES.MD]: { height: '34px', paddingKey: 'md', fontSizeKey: 'md' },
  [SIZES.LG]: { height: '40px', paddingKey: 'lg', fontSizeKey: 'lg' }
};

const SPINNER_DIAMETER = '16px';

const GHOST_DIAMETER = {
  [SIZES.SM]: '28px',
  [SIZES.MD]: '34px',
  [SIZES.LG]: '40px'
};

/**
 * `primary`/`secondary`: the "boxed" look — always a visible background, colors
 * resolved from theme.buttons.*. The border itself is only visible on `secondary`
 * (see `resolveBorderColor`).
 */
const solidVariantCss = ({
  theme, variant, size, isDisabled
}) => {
  const tokens = resolveSolidTokens(theme, variant, isDisabled);
  const borderColor = resolveBorderColor(theme, variant, isDisabled);
  const { height, paddingKey, fontSizeKey } = SOLID_SIZE[size];

  return css`
    height: ${height};
    padding: 0 ${theme.spacing[paddingKey]};
    font-size: ${theme.typography.fontSize[fontSizeKey]};
    background-color: ${tokens.background};
    color: ${tokens.color};
    border: 1px solid ${borderColor};

    &:hover:not(:disabled) {
      filter: brightness(0.94);
    }

    &:active:not(:disabled) {
      filter: brightness(0.88);
    }
  `;
};

/**
 * `link`: transparent, no border, sized to its content — for an action styled as inline
 * text (e.g. "Refresh", "Show more") that is NOT navigation. If something needs an
 * `href`/route change, it isn't this variant — that's a real link component, not a Button.
 * Color comes from `theme.text.link`, the same token already used elsewhere for
 * anchor-style text (replaces the legacy `LinkAsButton`/`ButtonLink`, both hardcoded to
 * `colors.blueCerulean` and never dark-mode aware).
 */
const linkVariantCss = ({ theme, size, isDisabled }) => css`
  height: auto;
  padding: 0 ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize[size]};
  background-color: transparent;
  border: none;
  color: ${isDisabled ? theme.buttons.disabled.color : theme.text.link};
  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

/**
 * `ghost`: transparent, borderless, circular hover highlight — for icon-only toolbar
 * triggers (the legacy `IconButton` look). Colors come from `theme.icons.actionBar.*`,
 * not `theme.buttons.*` — this family was never a "boxed" button to begin with.
 */
const ghostVariantCss = ({ theme, size, isDisabled }) => {
  const diameter = GHOST_DIAMETER[size];

  return css`
    width: ${diameter};
    height: ${diameter};
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: transparent;
    color: ${isDisabled ? theme.buttons.disabled.color : theme.icons.actionBar.color};

    &:hover:not(:disabled) {
      background-color: ${theme.icons.actionBar.background};
      color: ${theme.icons.actionBar.hover};
    }

    &:active:not(:disabled) {
      background-color: ${theme.icons.actionBar.background};
    }

    ${isDisabled && css`opacity: 0.5;`}
  `;
};

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: filter 0.15s ease, opacity 0.15s ease, background-color 0.15s ease;

  ${(props) => {
    if (props.variant === VARIANTS.GHOST) return ghostVariantCss(props);
    if (props.variant === VARIANTS.LINK) return linkVariantCss(props);
    return solidVariantCss(props);
  }}

  &:disabled {
    cursor: not-allowed;
  }

  ${({ isLoading }) => isLoading && css`
    cursor: wait;
  `}
`;
StyledButton.displayName = 'StyledButton';

export const Content = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  ${({ isLoading }) => isLoading && css`
    opacity: 0;
  `}
`;
Content.displayName = 'Content';

export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;

  & > svg > path,
  & > svg > g,
  & > svg > g > path,
  & > svg > g > g > g {
    fill: currentColor;
  }
`;
IconSlot.displayName = 'IconSlot';

export const Spinner = styled.span`
  position: absolute;
  top: calc(50% - ${SPINNER_DIAMETER} / 2);
  left: calc(50% - ${SPINNER_DIAMETER} / 2);
  width: ${SPINNER_DIAMETER};
  height: ${SPINNER_DIAMETER};
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  ${spin}
`;
Spinner.displayName = 'Spinner';
